import LandingLayout from "@/components/LandingLayout";
import { Button } from '@/components/ui/button';
import { Card, CardContent } from "@/components/ui/card";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
    RadioGroup,
    RadioGroupItem,
} from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { useAuth } from '@/context/AuthContext';
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from 'react';
import { useForm } from "react-hook-form";
import { Link } from 'react-router-dom';
import * as z from "zod";
import { SEO } from '@/components/SEO';
import { motion, AnimatePresence } from "framer-motion";
import { Textarea } from "@/components/ui/textarea";
const formSchema = z.object({
    name: z.string().min(2, {
        message: "Full name must be at least 2 characters.",
    }),
    email: z.string().email({
        message: "Please enter a valid email address.",
    }),
    reasonForJoining: z.enum(["I want to monitor my services", "I want to plan my projects", "I'm interested in both"], {
        required_error: "Please select an option that best describes your interest.",
        invalid_type_error: "Please select an option that best describes your interest.",
    }).nullable().refine((val) => val !== null, {
        message: "Please select an option that best describes your interest."
    }),
    aboutTeam: z.string().min(1, {
        message: "Please tell us about your team."
    })
});


const SuccessfulSignUp = () => {
    return (
        <div className="text-center w-full space-y-4 sm:space-y-6">
            <div className="relative max-w-sm mx-auto">
                <div className="absolute inset-0 blur-3xl opacity-20 bg-gradient-to-r from-violet-600 to-teal-400" />
                <h1 className="text-3xl sm:text-5xl font-bold bg-gradient-to-r from-violet-600 to-teal-400 text-transparent bg-clip-text relative">
                    You're In!
                </h1>
            </div>
            <p className="text-base sm:text-lg text-muted-foreground">
                Thanks for joining the waitlist. We'll notify you as soon as StatusBot is ready.
            </p>
            <Button>
                <Link to="/">Return Home</Link>
            </Button>
        </div>
    );
};

const AlreadyOnWaitlist = ({ setIsAlreadyOnWaitlist }) => {
    return (
        <div className="text-center w-full space-y-4 sm:space-y-6">
            <div className="relative">
                <div className="absolute inset-0 blur-3xl opacity-20 bg-gradient-to-r from-violet-600 to-teal-400" />
                <h1 className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-violet-600 to-teal-400 text-transparent bg-clip-text relative">
                    Already Registered
                </h1>
            </div>
            <p className="text-base sm:text-lg text-muted-foreground">
                This email is already on our waitlist. We'll be in touch soon!
            </p>
            <div className="flex justify-center gap-4">
                <Button onClick={() => setIsAlreadyOnWaitlist(false)}>
                    Try Different Email
                </Button>
                <Button variant="outline">
                    <Link to="/" onClick={() => localStorage.removeItem('waitlistData')}>
                        Return Home
                    </Link>
                </Button>
            </div>
        </div>
    );
};

const Error = ({ setIsError }) => {
    return (
        <div className="text-center w-full space-y-6">
            <div className="relative">
                <div className="absolute inset-0 blur-3xl opacity-20 bg-gradient-to-r from-rose-600 to-red-400" />
                <h1 className="text-4xl font-bold relative">
                    Oops! Something went wrong
                </h1>
            </div>
            <p className="text-lg text-muted-foreground">
                We encountered an error. Please try again or contact <a className="text-primary hover:underline underline-offset-4" href="mailto:help@status.bot">help@status.bot</a>
            </p>
            <div className="flex justify-center gap-4">
                <Button onClick={() => setIsError(false)}>
                    Try Again
                </Button>
                <Button variant="outline">
                    <Link to="/" onClick={() => localStorage.removeItem('waitlistData')}>
                        Return Home
                    </Link>
                </Button>
            </div>
        </div>
    );
};

const WaitlistSignUpPage = () => {
    const { axiosInstance } = useAuth();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isAlreadyOnWaitlist, setIsAlreadyOnWaitlist] = useState(false);
    const [isError, setIsError] = useState(false);
    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: "",
            email: "",
            reasonForJoining: null,
            aboutTeam: "",
        },
    });

    useEffect(() => {
        const email = localStorage.getItem('waitlistEmail');
        if (email) {
            form.setValue('email', email);
        }
    }, []);

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            const response = await axiosInstance.post('/waitlist', data);

            if (response.status === 201) {
                localStorage.removeItem('waitlistEmail');
                setIsSuccess(true);
            } else if (response.data.existingWaitlist) {
                setIsAlreadyOnWaitlist(true);
            } else {
                setIsError(true);
            }
        } catch (error) {
            console.error('Error submitting waitlist form:', error);
            setIsError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <SEO 
                title="Get Early Access"
                description="Join the StatusBot waitlist to get early access to AI-powered project management and service monitoring."
            />
            <LandingLayout>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-5xl mx-auto px-4 py-8 sm:py-16"
                >
                    <Card className="border-2 border-violet-500/20">
                        <CardContent className="py-8 px-4 sm:py-16 sm:px-32">
                            <AnimatePresence mode="wait">
                                {isSuccess && (
                                    <motion.div
                                        key="success"
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <SuccessfulSignUp />
                                    </motion.div>
                                )}
                                {isAlreadyOnWaitlist && (
                                    <motion.div
                                        key="already"
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <AlreadyOnWaitlist setIsAlreadyOnWaitlist={setIsAlreadyOnWaitlist} />
                                    </motion.div>
                                )}
                                {isError && (
                                    <motion.div
                                        key="error"
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <Error setIsError={setIsError} />
                                    </motion.div>
                                )}
                                {!isSuccess && !isAlreadyOnWaitlist && !isError && (
                                    <motion.div
                                        key="form"
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ duration: 0.3 }}
                                        className="space-y-6 sm:space-y-10"
                                    >
                                        <div className="text-center space-y-3 sm:space-y-4">
                                            <div className="relative">
                                                <div className="absolute inset-0 blur-3xl opacity-20 bg-gradient-to-r from-violet-600 to-teal-400" />
                                                <h1 className="text-3xl sm:text-5xl font-bold bg-gradient-to-r from-violet-600 to-teal-400 text-transparent bg-clip-text relative">
                                                    Transform Your Workflow
                                                </h1>
                                            </div>
                                            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
                                                Get early access to the next generation of AI-enhanced operations. Streamline your projects and monitor services with unprecedented intelligence.
                                            </p>
                                        </div>

                                        <Form {...form}>
                                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
                                                <FormField
                                                    control={form.control}
                                                    name="name"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <div className="text-sm font-medium leading-none mb-3">
                                                                Full name
                                                            </div>
                                                            <FormControl>
                                                                <Input placeholder="Bill Gates" {...field} />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />

                                                <FormField
                                                    control={form.control}
                                                    name="email"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <div className="text-sm font-medium leading-none mb-3">
                                                                Work email
                                                            </div>
                                                            <FormControl>
                                                                <Input
                                                                    placeholder="bill@microsoft.com"
                                                                    {...field}
                                                                    onChange={(e) => {
                                                                        field.onChange(e);
                                                                        localStorage.setItem('waitlistEmail', e.target.value);
                                                                    }}
                                                                />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />

                                                <div className="py-3">
                                                    <Separator />
                                                </div>

                                                <FormField
                                                    control={form.control}
                                                    name="reasonForJoining"
                                                    render={({ field }) => (
                                                        <FormItem className="space-y-3">
                                                            <div className="text-base font-medium leading-none mb-4">
                                                                Which aspect of StatusBot interests you most?
                                                            </div>
                                                            <FormControl>
                                                                <RadioGroup
                                                                    onValueChange={field.onChange}
                                                                    value={field.value}
                                                                    className="flex flex-col space-y-1 ml-1"
                                                                >
                                                                    <div className="flex items-center space-x-3">
                                                                        <FormControl>
                                                                            <RadioGroupItem value="I want to monitor my services" id="monitoring" />
                                                                        </FormControl>
                                                                        <label htmlFor="monitoring" className="font-normal text-sm text-muted-foreground cursor-pointer">
                                                                            I want to monitor my services
                                                                        </label>
                                                                    </div>
                                                                    <div className="flex items-center space-x-3">
                                                                        <FormControl>
                                                                            <RadioGroupItem value="I want to plan my projects" id="planning" />
                                                                        </FormControl>
                                                                        <label htmlFor="planning" className="font-normal text-sm text-muted-foreground cursor-pointer">
                                                                            I want to plan my projects
                                                                        </label>
                                                                    </div>
                                                                    <div className="flex items-center space-x-3">
                                                                        <FormControl>
                                                                            <RadioGroupItem value="I'm interested in both" id="both" />
                                                                        </FormControl>
                                                                        <label htmlFor="both" className="font-normal text-sm text-muted-foreground cursor-pointer">
                                                                            I'm interested in both
                                                                        </label>
                                                                    </div>
                                                                </RadioGroup>
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />

                                                <div className="py-3">
                                                    <Separator />
                                                </div>

                                                <FormField
                                                    control={form.control}
                                                    name="aboutTeam"
                                                    render={({ field }) => (
                                                        <FormItem className="space-y-3">
                                                            <div className="text-base font-medium leading-none mb-4">
                                                                Tell us about your team
                                                            </div>
                                                            <FormControl>
                                                                <Textarea
                                                                    placeholder="We have 100 engineers and are looking to monitor and organize our projects"
                                                                    {...field}
                                                                    className="min-h-[2.5rem] max-h-[10rem] resize-none transition-height duration-200"
                                                                    style={{ height: 'auto' }}
                                                                    onInput={(e) => {
                                                                        e.target.style.height = 'auto';
                                                                        e.target.style.height = `${e.target.scrollHeight}px`;
                                                                    }}
                                                                />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />

                                                <Button
                                                    type="submit"
                                                    disabled={isSubmitting}
                                                    className="w-full bg-gradient-to-r from-violet-600 to-teal-400 text-white hover:opacity-90 h-11 transition-opacity"
                                                >
                                                    {isSubmitting ? (
                                                        "Joining..."
                                                    ) : (
                                                        "Join Waitlist"
                                                    )}
                                                </Button>
                                                <p className="text-xs text-center text-muted-foreground">
                                                    By joining the waitlist, you agree to our{' '}
                                                    <Link to="/terms" className="underline hover:text-primary">Terms of Service</Link>{' '}
                                                    and{' '}
                                                    <Link to="/privacy" className="underline hover:text-primary">Privacy Policy</Link>.
                                                </p>
                                            </form>
                                        </Form>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </CardContent>
                    </Card>
                </motion.div>
            </LandingLayout>
        </>
    );
};

export default WaitlistSignUpPage